<template>
    <button
        ref="btn"
        type="button"
        class="nav-item"
        :class="{
            'is-active': active,
            'is-loading-chart': loading,
            'is-flyout-open': flyoutOpen,
            'is-sidebar-expanded': expanded,
            'has-tooltip-right': !expanded,
            'has-tooltip-arrow': !expanded,
        }"
        :data-tooltip="!expanded ? label : null"
        :aria-label="label"
        :aria-disabled="String(loading)"
        :aria-expanded="hasChildren ? String(childExpanded) : null"
        @mousedown="onMousedown"
    >
        <span
            v-if="active"
            class="nav-item-active-bar"
            aria-hidden="true"
        ></span>
        <svg
            class="nav-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
        >
            <path :d="icon" />
        </svg>
        <span class="nav-item-label">{{ label }}<span
            v-if="badge"
            class="nav-item-badge"
        > {{ badge }}</span></span>
        <ArrowDown
            v-if="hasChildren"
            class="nav-chevron"
            :class="{ 'is-rotated': expanded && childExpanded }"
        />
    </button>
</template>

<script>
import ArrowDown from "@/components/svg/ArrowDown.vue";

export default {
    name: "SidebarNavItem",
    components: { ArrowDown },
    props: {
        label: { type: String, required: true },
        // SVG path data feather-style (stroke 2px, viewBox 24x24).
        icon: { type: String, required: true },
        // Selected: muestra accent-bar a la izquierda.
        active: { type: Boolean, default: false },
        // Sidebar expanded global: controla visibilidad de label/chevron y
        // activación de tooltips (sólo collapsed).
        expanded: { type: Boolean, default: false },
        // chartLoaded gating: aplica estilo loading + ignora click logic en
        // el padre. El padre decide si emit click sigue adelante.
        loading: { type: Boolean, default: false },
        // Si tiene hijos, mostramos chevron y aria-expanded sigue childExpanded.
        hasChildren: { type: Boolean, default: false },
        // Acordeón abierto (sólo aplica cuando expanded global = true).
        childExpanded: { type: Boolean, default: false },
        // Flyout abierto (sólo aplica cuando expanded global = false).
        flyoutOpen: { type: Boolean, default: false },
        // Texto secundario inline (e.g., "(beta)") en italic muted al lado
        // del label. Vacío = no se renderiza.
        badge: { type: String, default: "" },
    },
    methods: {
        onMousedown() {
            // Emitimos el button DOM element directo (no el event) porque
            // event.currentTarget queda null al re-emitirse via $emit chain
            // de Vue — el ref interno siempre es el elemento real.
            this.$emit("click", this.$refs.btn);
        },
    },
};
</script>

<style lang="scss" scoped>
.nav-item {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background-color: transparent;
    border: none;
    border-radius: var(--sidebar-radius, 6px);
    color: var(--sidebar-ink-muted, #667085);
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    text-align: left;
    cursor: pointer;
    user-select: none;
    transition: background-color var(--app-transition-fast),
        color var(--app-transition-fast);

    &:hover {
        background-color: var(--sidebar-hover, rgba(16, 24, 40, 0.03));
        color: var(--sidebar-ink, #1d2939);
    }

    &:focus-visible {
        outline: 2px solid var(--sidebar-accent, #0d5154);
        outline-offset: -2px;
    }

    &.is-active {
        color: var(--sidebar-ink, #1d2939);
        font-weight: 600;
        // Accent-bar es un <span> dedicado, no ::before — Bulma tooltip ya
        // ocupa ::before para su bubble.
    }

    &.is-loading-chart {
        color: var(--sidebar-ink-faint, #98a2b3);
        cursor: progress;
    }

    &.is-flyout-open {
        background-color: var(--sidebar-accent-wash, rgba(13, 81, 84, 0.06));
        color: var(--sidebar-ink, #1d2939);
    }
}

.nav-item-active-bar {
    position: absolute;
    top: 6px;
    bottom: 6px;
    left: 2px;
    width: 3px;
    border-radius: 1.5px;
    background-color: var(--sidebar-accent, #0d5154);
    pointer-events: none;
}

.nav-icon {
    flex-shrink: 0;
    width: var(--sidebar-icon-size, 20px);
    height: var(--sidebar-icon-size, 20px);
}

.nav-item-label {
    flex: 1;
    opacity: 0;
    max-width: 0;
    overflow: hidden;
    white-space: nowrap;
    transition: opacity var(--app-transition-fast) 0.05s,
        max-width var(--app-transition-base);
}

.nav-item-badge {
    font-style: italic;
    font-size: 12px;
    font-weight: 400;
    color: var(--sidebar-ink-faint, #98a2b3);
}

// El estado expanded del sidebar global se propaga via prop → class local
// (`.is-sidebar-expanded`) porque scoped CSS no atraviesa boundaries de
// componente. La clase vive en el mismo button, así el descendant selector
// resuelve dentro del scope local.
.nav-item.is-sidebar-expanded .nav-item-label {
    opacity: 1;
    max-width: 220px;
}

.nav-chevron {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: var(--sidebar-ink-faint, #98a2b3);
    opacity: 0;
    transition: opacity var(--app-transition-fast),
        transform var(--app-transition-base);

    &.is-rotated {
        transform: rotate(180deg);
    }
}

.nav-item.is-sidebar-expanded .nav-chevron {
    opacity: 1;
}
</style>
